import { selectCompanyBranding } from "app/modules/system/whiteLabelling/CompanyBrandingSlice";
import { Form, Formik } from "formik";
import { useSelector } from "react-redux";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import * as Yup from "yup";
import { changeClientPortalUserPassword, selectUpdatingPassword, selectPasswordReset } from "./ResetPasswordSlice";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { useEffect } from "react";
import CPLPasswordInput from "cpl-react-common/components/Inputs/CPLPasswordInput";
import { PasswordChangeProps } from "app/modules/account/components/cards/components/UpdatePasswordForm";

const passwordFormValidationSchema = Yup.object().shape({
  currentPassword: Yup.string()
    .min(3, "Minimum 3 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Password is required"),
  newPassword: Yup.string().min(3, "Minimum 3 symbols").max(50, "Maximum 50 symbols").required("Password is required"),
  passwordConfirmation: Yup.string()
    .min(3, "Minimum 3 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Password is required")
    .oneOf([Yup.ref("newPassword")], "Passwords must match"),
});

const ResetPasswordPage = () => {
  const thunkDispatch = useThunkDispatch();
  const companyBranding = useSelector(selectCompanyBranding);
  const updatingPassword = useSelector(selectUpdatingPassword);
  const passwordReset = useSelector(selectPasswordReset);

  const updatePasswordInitialValues: PasswordChangeProps = {
    currentPassword: "",
    newPassword: "",
    passwordConfirmation: "",
  };

  const updatePasswordSubmit = (values: PasswordChangeProps) => {
    thunkDispatch(changeClientPortalUserPassword(values.newPassword));
  };

  useEffect(() => {
    if (passwordReset) {
      window.location.href = "/dashboard";
    }
  }, [passwordReset]);

  return (
    <div className="d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed">
      {/* begin::Content */}
      <div className="d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20">
        {/* begin::Wrapper */}
        <div className="w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto">
          <ActivityIndicator isBusy={updatingPassword}>
            <Formik
              initialValues={updatePasswordInitialValues}
              validationSchema={passwordFormValidationSchema}
              onSubmit={updatePasswordSubmit}
            >
              {(formikProps) => (
                <Form>
                  {/* begin::Heading */}
                  <div className="text-center mb-10">
                    <h1 className="heading-text mb-3">{`Register with ${
                      companyBranding?.clientPortalName ?? "CPL Client Portal"
                    }`}</h1>
                  </div>
                  {/* begin::Heading */}
                  {/* begin::Form group */}
                  <div className="fv-row mb-3">
                    <label className="col-sm-12 form-label fw-bolder fs-6 mb-0">
                      Current Password
                      <CPLPasswordInput
                        getFieldProps={formikProps.getFieldProps}
                        id="currentPassword"
                        data-test-id="reset-password-form__current-password-input"
                      />
                    </label>
                  </div>
                  <div className="fv-row mb-10">
                    <label className="col-sm-12 form-label fw-bolder fs-6 mb-0">
                      New Password
                      <CPLPasswordInput
                        getFieldProps={formikProps.getFieldProps}
                        id="newPassword"
                        data-test-id="reset-password-form__new-password-input"
                      />
                    </label>
                  </div>
                  <div className="fv-row mb-10">
                    <label className="col-sm-12 form-label fw-bolder fs-6 mb-0">
                      Confirm Password
                      <CPLPasswordInput
                        getFieldProps={formikProps.getFieldProps}
                        id="passwordConfirmation"
                        data-test-id="reset-password-form__confirm-password-input"
                      />
                    </label>
                  </div>
                  {/* end::Form group */}
                  {/* begin::Action */}
                  <div className="text-center">
                    <button
                      type="submit"
                      className="cpl-btn btn-lg cpl-btn-primary w-100 mb-5"
                      data-test-id="reset-password-form__submit-button"
                    >
                      Continue
                    </button>
                  </div>
                  {/* end::Action */}
                </Form>
              )}
            </Formik>
          </ActivityIndicator>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
